import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from '@municipality/ui';
import { SuperAdminLayout } from './components/SuperAdminLayout';
import { antdTheme } from './theme';
import { Login } from './pages/Login';
import { Activate } from './pages/Activate';
import Unauthorized from './pages/Unauthorized';
import { EmailService } from '@municipality/firebase';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 2 * 60 * 1000, retry: 1 },
  },
});

console.log('🚀 App: Starting super admin application');

// Configure EmailService with environment variable at app startup
const sendGridFunctionUrl = import.meta.env.VITE_SENDGRID_FUNCTION_URL;
if (sendGridFunctionUrl) {
  EmailService.configure(sendGridFunctionUrl);
  console.log('📧 App: Email service configured');
} else {
  console.warn('⚠️ App: VITE_SENDGRID_FUNCTION_URL not found in environment variables');
}

// Direct imports for testing
import Dashboard from './pages/Dashboard';
import Municipalities from './pages/Municipalities';
import MunicipalityDetails from './pages/MunicipalityDetails';
import Users from './pages/Users';
import UserMunicipalityAccess from './pages/UserMunicipalityAccess';
import Translations from './pages/Translations';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import { KnowledgeBaseCategories } from './pages/KnowledgeBaseCategories';
import { KnowledgeBaseCategoryForm } from './pages/KnowledgeBaseCategoryForm';
import { KnowledgeBaseTags } from './pages/KnowledgeBaseTags';
import { KnowledgeBaseTagForm } from './pages/KnowledgeBaseTagForm';
import { KnowledgeBase } from './pages/KnowledgeBase';
import { KnowledgeBaseArticles } from './pages/KnowledgeBaseArticles';
import { KnowledgeBaseArticleForm } from './pages/KnowledgeBaseArticleForm';
import Chat from './pages/Chat';
import AccessDenied from './pages/AccessDenied';
import { UserRoles } from './pages/UserRoles';
import { RoleForm } from './pages/RoleForm';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SuperAdminRole } from '@municipality/types';

// Custom error boundary with logging
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('🚀 App: Error boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚀 App: Error boundary error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '24px' }}>
          <h1>Application Error</h1>
          <p>Something went wrong in the application.</p>
          <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            {this.state.error?.toString()}
          </pre>
          <button 
            onClick={() => this.setState({ hasError: false, error: undefined })}
            style={{ marginTop: '10px', padding: '8px 16px' }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppRoutes: React.FC = () => {
  console.log('🚀 App: Rendering app routes');
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route element={<SuperAdminLayout />}>
        <Route
          path="dashboard"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="chat"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="municipalities"
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN, SuperAdminRole.ADMINISTRATOR]}>
              <Municipalities />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="municipalities/:id" 
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN, SuperAdminRole.ADMINISTRATOR]}>
              <MunicipalityDetails />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="users" 
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN, SuperAdminRole.ADMINISTRATOR, SuperAdminRole.REGION_MANAGER]}>
              <Users />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="users/:userId/municipalities" 
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN, SuperAdminRole.ADMINISTRATOR, SuperAdminRole.REGION_MANAGER]}>
              <UserMunicipalityAccess />
            </ProtectedRoute>
          } 
        />
        <Route
          path="user-roles"
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN]}>
              <UserRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path="user-roles/create"
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN]}>
              <RoleForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="user-roles/edit/:id"
          element={
            <ProtectedRoute requiredRoles={[SuperAdminRole.SUPER_ADMIN]}>
              <RoleForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="translations"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <Translations />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/categories"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/categories/create"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseCategoryForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/categories/edit/:id"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseCategoryForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/tags"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseTags />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/tags/create"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseTagForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/tags/edit/:id"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseTagForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBase />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/articles"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <KnowledgeBaseArticles />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/articles/create"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.CONTENT_MANAGER,
              ]}
            >
              <KnowledgeBaseArticleForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base/articles/edit/:id"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.CONTENT_MANAGER,
              ]}
            >
              <KnowledgeBaseArticleForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute
              requiredRoles={[
                SuperAdminRole.SUPER_ADMIN,
                SuperAdminRole.ADMINISTRATOR,
                SuperAdminRole.REGION_MANAGER,
                SuperAdminRole.CONTENT_MANAGER,
                SuperAdminRole.ACCOUNTANT,
                SuperAdminRole.SUPPORT,
              ]}
            >
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

export const App: React.FC = () => {
  console.log('🚀 App: Main App component rendering');
  
  return (
    <ConfigProvider 
      theme={antdTheme}
      // React 19 compatibility configuration
      componentSize="middle"
      space={{ size: 'middle' }}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <AppErrorBoundary>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
              </AppErrorBoundary>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}; 